/**
 * Transforms a GoodsShipment GraphQL object to/from the form state.
 *
 * IMPORTANT: toSchema() must only output fields valid on the mutation input.
 * Do NOT spread the original — it contains federated/queried fields (like
 * importer.name, __typename) that the mutation rejects.
 *
 * @see LLD Section 3.2 — Transform Classes
 */
import { BaseTransformer } from './BaseTransformer';
import { GoodsShipmentFormState } from '../schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from './GoodsItemDetailTransformer';
import { getEditedGoodsItemsForShipment } from './goodsItemFormRef';
import { getTransportationSnapshot } from './transportationFormRef';
import { TransportationTransformer } from './TransportationTransformer';
import { stripTypeNames } from '@xbcb/js-utils/dist';
import { TransportContractDocumentType } from '__generated__/graphql';
import { getCurrencyForCountryCode } from 'libs/currency/getCurrencyForCountryCode';
import { SupportedCountryAbbreviation } from '@xbcb/shared-types';
import type {
  goodsShipmentFieldsFragment,
  GoodsShipmentInput,
  BorderTransportMeansInput,
  CostInput,
  TransportEquipmentInput,
} from '__generated__/graphql';

/** Use the codegen fragment type as the API data shape */
export type GoodsShipmentApiData = goodsShipmentFieldsFragment;

const transportationTransformer = new TransportationTransformer();

/**
 * Transport contract document types owned by the Transportation tab
 * (Option B split — see D451531139). The GoodsShipment form owns the
 * primary/other doc types; the Transportation tab owns bills of lading.
 */
const TRANSPORTATION_OWNED_DOC_TYPES = new Set<TransportContractDocumentType>([
  TransportContractDocumentType.MASTER_BILL_OF_LADING,
  TransportContractDocumentType.HOUSE_BILL_OF_LADING,
  TransportContractDocumentType.HOUSE_WAYBILL,
]);

/*
 * Stamps shipment-level fields onto an individual item at save time.
 * Extensible: add new stamp logic here as shipment→item fields are identified.
 *
 * Currency is stored per item (customsValue.currency), so it is only stamped
 * onto items that already have a customsValue.value. We intentionally do NOT
 * write currency onto a value-less item: that would persist a currency with no
 * amount (customsValue.value == null), which is semantically meaningless and
 * risks CDS validation issues. Trade-off (accepted): a manually chosen currency
 * on an item that has no value yet is not persisted; on reload the shipment
 * currency falls back to the destination-country default (see toForm). This is
 * fine in practice because currency almost always equals the country default,
 * and an item's value is required before the declaration can be completed.
 */
const stampShipmentFields = (
  item: Record<string, any>,
  formData: GoodsShipmentFormState,
): Record<string, any> => {
  const currency = formData.currency;
  if (!currency) return item;

  // Only items with a value carry currency (see function doc for the trade-off).
  if (item.customsValue?.value != null) {
    item.customsValue = { ...item.customsValue, currency };
  }

  // Stamp currency onto each valuationAdjustment amount
  if (item.valuationAdjustment) {
    item.valuationAdjustment = item.valuationAdjustment.map((a: any) =>
      a.amount ? { ...a, amount: { ...a.amount, currency } } : a,
    );
  }

  return item;
};

export class CuDeGoodsShipmentTransformer extends BaseTransformer<
  GoodsShipmentApiData,
  GoodsShipmentFormState,
  GoodsShipmentInput
> {
  toForm(source: GoodsShipmentApiData): GoodsShipmentFormState {
    const consignment = source.consignment?.[0];
    const doc = consignment?.transportContractDocument?.[0];
    const tradeTerms = source.tradeTerms?.[0];
    const supportingDocs = source.supportingDocument ?? [];
    const previousDocs = source.previousDocument ?? [];
    const importCountryCode = source.importCountry?.[0]?.country ?? null;

    return {
      transportDoc: {
        type: doc?.type ?? null,
        identification: doc?.identification ?? null,
      },
      supportingDocs:
        supportingDocs.length > 0
          ? supportingDocs.map((d) => ({
              type: d.type ?? null,
              identification: d.identification ?? null,
            }))
          : [{ type: null, identification: null }],
      previousDocs:
        previousDocs.length > 0
          ? previousDocs.map((d) => ({
              type: d.type ?? null,
              identification: d.identification ?? null,
              category: d.category ?? null,
              line: d.line ?? null,
            }))
          : [{ type: null, identification: null, category: null, line: null }],
      incoterms: tradeTerms?.condition ?? null,
      incotermPlace: tradeTerms?.location ?? null,
      // Currency is stored per-item (goodsItem[].customsValue.currency). When
      // there are no goods items to read from, fall back to the destination
      // country's default currency so the field is never blank on reload.
      // importCountry is set at shipment creation and is never editable, so
      // this is stable.
      currency:
        source.goodsItem?.[0]?.customsValue?.currency ??
        consignment?.valueAmount?.currency ??
        (importCountryCode
          ? getCurrencyForCountryCode(
              importCountryCode as unknown as SupportedCountryAbbreviation,
            )
          : null),
      // `value` is deprecated; fall back to it for pre-cutover records.
      invoiceValue:
        consignment?.valueAmount?.value ?? consignment?.value ?? null,
      exportCountry: source.exportCountry?.[0]?.country ?? null,
      importCountry: importCountryCode,
      adjustments: (source.valuationAdjustment ?? []).map((a) => ({
        type: a.addition ?? null,
        amount: a.amount?.value ?? null,
        currency: a.amount?.currency ?? null,
        percentage: a.percentage ?? null,
      })),
      importer: source.importer?.[0]
        ? { id: source.importer[0].id, name: source.importer[0].name ?? null }
        : null,
      buyer: source.buyer?.[0]
        ? { id: source.buyer[0].id, name: source.buyer[0].name ?? null }
        : null,
      consignee: source.consignee?.[0]
        ? { id: source.consignee[0].id, name: source.consignee[0].name ?? null }
        : null,
    };
  }

  toSchema(
    formData: GoodsShipmentFormState,
    original: GoodsShipmentApiData,
    shipmentIndex = 0,
  ): GoodsShipmentInput {
    const originalConsignment = original.consignment?.[0];

    // Operator source of truth: when the Transportation tab has been
    // edited this session (snapshot present, shipment 0), the Export /
    // Import Country typed there win over the main GoodsShipment form —
    // that tab is the only place these are editable. An empty value is
    // treated as an explicit clear (null) rather than falling back, so
    // clearing the field on the Transportation tab persists. `destination`
    // is the TransportationForm field that maps to importCountry.
    const transportCountrySnapshot =
      shipmentIndex === 0 ? getTransportationSnapshot() : null;
    const exportCountryCode = transportCountrySnapshot
      ? transportCountrySnapshot.formData.exportCountry?.trim() || null
      : formData.exportCountry;
    const importCountryCode = transportCountrySnapshot
      ? transportCountrySnapshot.formData.destination?.trim() || null
      : formData.importCountry;

    // CostInput requires a currency, so the value is only sent with one; null
    // when empty so clearing the field persists.
    const invoiceCurrency =
      formData.currency ??
      (importCountryCode
        ? getCurrencyForCountryCode(
            importCountryCode as unknown as SupportedCountryAbbreviation,
          )
        : null);
    const valueAmount: CostInput | null =
      formData.invoiceValue != null && invoiceCurrency
        ? { value: formData.invoiceValue, currency: invoiceCurrency }
        : null;

    // Filter out empty supporting docs (both fields null/empty)
    const supportingDocument = formData.supportingDocs
      .filter((d) => d.type || d.identification)
      .map((d) => ({
        type: d.type,
        identification: d.identification,
      }));

    // Filter out empty previous docs (all fields null/empty)
    const previousDocument = formData.previousDocs
      .filter((d) => d.type || d.identification || d.category || d.line)
      .map((d) => ({
        type: d.type,
        identification: d.identification,
        category: d.category,
        line: d.line,
      }));

    return {
      tradeTerms: [
        {
          condition: formData.incoterms,
          location: formData.incotermPlace,
        },
      ],
      supportingDocument:
        supportingDocument.length > 0 ? supportingDocument : undefined,
      previousDocument:
        previousDocument.length > 0 ? previousDocument : undefined,
      // TODO: When apportioning is implemented, distribute these shipment-level
      // adjustments proportionally among goodsItem[].valuationAdjustment on save.
      valuationAdjustment: formData.adjustments
        .filter((a) => a.type)
        .map((a) => ({
          addition: a.type,
          amount:
            a.amount != null
              ? { value: a.amount, currency: a.currency ?? formData.currency }
              : null,
          percentage: a.percentage,
        })),
      exportCountry: [{ country: exportCountryCode }],
      importCountry: [{ country: importCountryCode }],
      consignment: [
        (() => {
          // Primary transport doc from the GoodsShipment form. If its type
          // is a bill of lading owned by the Transportation tab AND a
          // transportation snapshot exists, the Transportation tab's
          // version wins (avoids duplicates).
          //
          // Gate the transportation snapshot to shipment 0 only. The
          // Transportation tab currently hardcodes `goodsShipment[0]`
          // (see TransportationTransformer.toForm), so applying the
          // snapshot to shipments 1..N would clobber their MBL/HBL,
          // conveyance, and equipment with shipment 0's edits. Shipments
          // 1..N take the pass-through branch below and keep their own
          // original data. If Transportation becomes per-shipment in the
          // future, key the snapshot by shipmentIndex in
          // transportationFormRef instead of gating here.
          const transportSnapshot =
            shipmentIndex === 0 ? getTransportationSnapshot() : null;

          const primaryDoc =
            formData.transportDoc.type || formData.transportDoc.identification
              ? [
                  {
                    type: formData.transportDoc
                      .type as TransportContractDocumentType,
                    identification: formData.transportDoc.identification,
                  },
                ]
              : [];

          if (!transportSnapshot) {
            // Transportation tab not visited/editable this session:
            // preserve current behavior — form doc + original pass-through.
            return {
              valueAmount,
              transportContractDocument: primaryDoc,
              // Preserve non-editable fields managed by Transportation tab.
              borderTransportationMean:
                originalConsignment?.borderTransportationMean
                  ? (stripTypeNames(
                      originalConsignment.borderTransportationMean,
                    ) as unknown as BorderTransportMeansInput[])
                  : undefined,
              transportEquipment: originalConsignment?.transportEquipment
                ? (stripTypeNames(
                    originalConsignment.transportEquipment,
                  ) as unknown as TransportEquipmentInput[])
                : undefined,
            };
          }

          // Transportation tab was visited — merge its edits (Option B):
          // Transportation owns bill-of-lading doc types + conveyance
          // fields; GoodsShipment form owns the primary doc (non-bill types).
          const transportSchema = transportationTransformer.toSchema(
            transportSnapshot.formData,
            transportSnapshot.original,
          );
          const mergedConsignment =
            transportSchema.goodsShipment?.[0]?.consignment?.[0];

          const billDocs = (
            mergedConsignment?.transportContractDocument ?? []
          ).filter(
            (d: { type?: TransportContractDocumentType | null }) =>
              d.type != null && TRANSPORTATION_OWNED_DOC_TYPES.has(d.type),
          );
          const nonBillPrimaryDoc = primaryDoc.filter(
            (d) =>
              d.type == null || !TRANSPORTATION_OWNED_DOC_TYPES.has(d.type),
          );

          return {
            valueAmount,
            // stripTypeNames on billDocs: TransportationTransformer's
            // toSchema falls back to the original consignment's docs
            // when the form's bills are cleared, and those carry
            // __typename from the query fragment. The mutation input
            // rejects __typename, so strip it here (same reason the
            // pass-through branch above wraps its preserved fields).
            transportContractDocument: [
              ...nonBillPrimaryDoc,
              ...(stripTypeNames(billDocs) as unknown as Array<{
                type?: TransportContractDocumentType;
                identification?: string | null;
              }>),
            ],
            borderTransportationMean:
              mergedConsignment?.borderTransportationMean
                ? (stripTypeNames(
                    mergedConsignment.borderTransportationMean,
                  ) as unknown as BorderTransportMeansInput[])
                : undefined,
            transportEquipment: mergedConsignment?.transportEquipment
              ? (stripTypeNames(
                  mergedConsignment.transportEquipment,
                ) as unknown as TransportEquipmentInput[])
              : undefined,
            containerized: mergedConsignment?.containerized,
          };
        })(),
      ],
      importer: formData.importer ? [{ id: formData.importer.id }] : undefined,
      buyer: formData.buyer ? [{ id: formData.buyer.id }] : undefined,
      consignee: formData.consignee
        ? [{ id: formData.consignee.id }]
        : undefined,
      goodsItem: (() => {
        const edited = getEditedGoodsItemsForShipment(shipmentIndex);
        const itemTransformer = new GoodsItemDetailTransformer();
        return original.goodsItem?.map((item) => {
          let result;
          const entry = edited.get(item.sequence!);
          if (entry) {
            result = {
              ...itemTransformer.toSchema(entry.formData, entry.original),
            };
          } else {
            // Unvisited: round-trip through transformer to emit only mutation-valid fields
            result = {
              ...itemTransformer.toSchema(itemTransformer.toForm(item), item),
            };
          }
          return stampShipmentFields(result, formData);
        });
      })(),
    };
  }
}
